<template>
    <svg id="comb-map-svg" ref="svg" width="100%" height="100%" style="background-color:black" viewBox="0 0 580 330">
        <svg v-for="combID in Object.keys(combs)" v-bind:key="combID" :id="combID"
             :x="getCombX(combID) - 5.5/2"
             :y="getCombY(combID) - 4.8/2"
             width="5.5px"
             height="4.8px"
             @click="onCombClick(combID)"
        >
            <polygon :class="{'pointer-polygon': combs[combID].last_hash}"
                     width="5.5"
                     height="4.8"
                     class="hex"
                     :fill="combs[combID].color"
                     :points="getCombPoints()">
            </polygon>
        </svg>
    </svg>
</template>

<script>

    import * as d3 from "d3";
    import mapData from '@/assets/comb-map-data.json'
    import Constants from '@/utils/constants';
    import Common from '@/utils/Common.vue'

    const COMB_COLOR = "rgba(30, 42, 53, 1)";
    const COMB_FILLED_COLOR = "rgba(255, 0, 67, 1)";
    const COMB_FORECAST_FILLED_COLOR = "rgb(117, 32, 250)";

    const HEX_WIDTH = 5.5;
    const HEX_HEIGHT = 4.8;

    export default {
        props: {
            feed: {
                type: Array
            }
        },
        components: {},
        mixins: [Common],
        data() {
            return {
                combs: {},
                pulseHexs: [],
                svg: null,
                clearCombsInterval: null,
                pageName: 'Realtime Malware Detection Widget'
            }
        },
        methods: {
            getCombX(combID) {
                return this.combs[combID].left;
            },
            getCombY(combID) {
                return this.combs[combID].top;
            },
            getCombOrigin(x, y) {
                let combOriginX = x + (HEX_WIDTH / 2);
                let combOriginY = y + (HEX_HEIGHT / 2);
                return {x: combOriginX, y: combOriginY};
            },
            createCombId(x, y) {
                var origin = this.getCombOrigin(x, y);
                return origin.x.toFixed(4) + "-" + origin.y.toFixed(4);
            },
            createMap() {
                var combs = {};
                for (var i = 0; i < mapData.length; i++) {
                    let id = this.createCombId(mapData[i].left, mapData[i].top);
                    combs[id] = {
                        left: mapData[i].left,
                        top: mapData[i].top,
                        color: COMB_COLOR,
                        id: id
                    };
                }
                this.combs = combs;
            },
            getCombPoints() {
                var point1X = HEX_WIDTH * 0.25;
                var point1Y = 0;
                var p1 = "" + point1X + "," + point1Y;

                var point2X = HEX_WIDTH * 0.75;
                var point2Y = 0;
                var p2 = "" + point2X + "," + point2Y;

                var point3X = HEX_WIDTH;
                var point3Y = HEX_HEIGHT * 0.5;
                var p3 = "" + point3X + "," + point3Y;

                var point4X = HEX_WIDTH * 0.75;
                var point4Y = HEX_HEIGHT;
                var p4 = "" + point4X + "," + point4Y;

                var point5X = HEX_WIDTH * 0.25;
                var point5Y = HEX_HEIGHT;
                var p5 = "" + point5X + "," + point5Y;

                var point6X = 0;
                var point6Y = HEX_HEIGHT * 0.5;
                var p6 = "" + point6X + "," + point6Y;

                return p1 + " " + p2 + " " + p3 + " " + p4 + " " + p5 + " " + p6;
            },
            getCoordinatesFromGeoLocation(lat, lon) {
                var width = 580;
                var height = 330;

                var x = (width * (lon + 180) / (2 * 180)) - 45;
                var y = (height * (-1 * lat + 90) / (2 * 90)) + 52;

                return {x: x, y: y};
            },
            getDistanceBetweenPoints(x1, y1, x2, y2) {
                var a = x1 - x2;
                var b = y1 - y2;
                return Math.sqrt(a * a + b * b);
            },
            findClosestComb(x, y) {
                var minDistance = 99999;
                var closestCombId;

                for (let combId in this.combs) {
                    let combOriginX = this.getCombX(combId) + (HEX_WIDTH / 2);
                    let combOriginY = this.getCombY(combId) + (HEX_HEIGHT / 2);
                    let distance = this.getDistanceBetweenPoints(x, y, combOriginX, combOriginY);

                    if (distance < minDistance) {
                        minDistance = distance;
                        closestCombId = combId;
                    }
                }

                return this.combs[closestCombId];
            },
            showPoint(newFeedData) {
                let longitude = newFeedData.geoPoint.longitude;
                let latitude = newFeedData.geoPoint.latitude;

                var coordinates = this.getCoordinatesFromGeoLocation(parseFloat(latitude), parseFloat(longitude));
                let comb = this.findClosestComb(coordinates.x, coordinates.y);
                comb.color = COMB_FILLED_COLOR;
                comb.filled = true;
                comb.lastTimestamp = Date.now();
                comb.last_hash = newFeedData.hash;
                this.addPulse(comb);
            },
            getCombWithOrigin(originX, originY) {
                return this.combs[originX + "-" + originY];
            },
            showForecast(forecast) {
                this.clearMap();
                for (let i = 0; i < forecast.length; i++) {
                    let item = forecast[i];
                    let comb = this.getCombWithOrigin(item.x.toFixed(4), item.y.toFixed(4));
                    if (!comb) continue;
                    comb.color = COMB_FORECAST_FILLED_COLOR;
                    comb.filled = true;
                }
            },
            clearMap() {
                for (let combId in this.combs) {
                    this.combs[combId].color = COMB_COLOR;
                    this.combs[combId].filled = false;
                }
            },
            addPulse(comb) {
                var id = "" + Date.now();
                var newData = [{
                    id: id,
                    combID: comb.id,
                    color: COMB_FILLED_COLOR
                }];

                var self = this;
                var svg = this.svg;

                var join2 = svg
                    .selectAll('.pulse')
                    .data(newData, function (d) {
                        return d.id
                    });

                join2
                    .enter()
                    .append('circle')
                    .attr('class', 'pulse')
                    .attr("cx", function (d) {
                        return self.getCombX(comb.id)
                    })
                    .attr("cy", function (d) {
                        return self.getCombY(comb.id)
                    })
                    .attr("r", "3px")
                    .attr("stroke", COMB_FILLED_COLOR)
                    .attr("stroke-width", "2")
                    .attr("stroke-opacity", "1")
                    .attr("fill", COMB_FILLED_COLOR)
                    .attr("fill-opacity", "0.7")
                    .transition()
                    .duration(800)
                    .attr("r", "24px")
                    .attr("fill-opacity", "0")
                    .attr("stroke-opacity", "0")
                    .attr("stroke-width", "1")
                    .remove()
            },
            clearOldCombs() {
                for (let combID in this.combs) {
                    let comb = this.combs[combID];
                    if (comb.lastTimestamp) {
                        let diff = Date.now() - comb.lastTimestamp;
                        if (diff > (120 * 1000)) {
                            comb.color = COMB_COLOR;
                            comb.filled = false;
                            comb.lastTimestamp = null;
                        }
                    }
                }
            },
            onCombClick(combID) {
                if (this.combs[combID]["last_hash"] !== undefined) {
                    this.verdictSearchHandlerCommon(this.combs[combID]["last_hash"], this, this.pageName, false);
                    // this.openInNewTab(Constants.VALKYRIE_SHA1_URL + this.combs[combID]["last_hash"]);
                }

            }
        },
        beforeDestroy() {
            clearInterval(this.clearCombsInterval);
        },
        mounted() {
            this.createMap();
            this.svg = d3.select("#comb-map-svg");
            var self = this;
            this.clearCombsInterval = setInterval(function () {
                self.clearOldCombs();
            }, 10 * 1000);
        }
    }
</script>

<style>

    .hex {
        fill-opacity: 1;
    }

    .pulse {
        pointer-events: none;
    }

    .pointer-polygon {
        cursor: pointer;
        z-index: 10;
        pointer-events: all;

    }

</style>



// WEBPACK FOOTER //
// src/components/world-map/CombWorldMap.vue